import { useCallback, useEffect, useState } from "react";

import { styled } from "styled-components";

import { Modal } from "@similarweb/ui-components";

import { useInfiniteScrollFetch } from "app/components/hooks/useInfiniteScrollFetch";
import { ErrorMessage } from "app/components/impersonate-popup/components/ErrorMessage";
import { SearchContainer } from "app/components/impersonate-popup/components/SearchContainer";
import { useImpersonateUser } from "app/components/impersonate-popup/hooks/useImpersonate";
import { useUserSearch } from "app/components/impersonate-popup/hooks/useUserSearch";
import { translate } from "app/filters/i18nFilter";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";

import { SearchResultsDisplay } from "./components/SearchResultsDisplay";
import { DEFAULT_SEARCH_IN_FIELD, searchableColumns } from "./helpers";
import { SearchColumnDropdown } from "./SearchColumnDropdown";
import type { INewImpersonateModalProps } from "./types";

const SCROLL_THRESHOLD_PX = 100;

const ModalStyles: IProModalCustomStyles = {
    content: {
        boxSizing: "border-box",
        width: "800px",
        padding: "0",
        height: 566,
        overflow: "hidden",
    },
    overlay: {
        zIndex: 1072,
    },
};

const ModalContent = styled.div`
    box-sizing: border-box;
    width: 800px;
    padding: 0;
    height: 566px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const FormWrapper = styled.div`
    display: flex;
    gap: 16px;
    margin-top: 32px;
    margin-bottom: 32px;
    padding: 0 24px;
`;

const ScrollableContent = styled.div`
    overflow-y: auto;
    overflow-x: hidden;
    flex: 1;
    min-height: 0;
`;

const StyledModalCloseButton = styled(Modal.CloseButton)`
    top: 16px;
`;

export const ImpersonateModal = ({ isOpen, onClose }: INewImpersonateModalProps) => {
    const [selectedColumn, setSelectedColumn] = useState(DEFAULT_SEARCH_IN_FIELD);
    const {
        search,
        fetchUsers,
        loading,
        tableData,
        error,
        setSearchInField,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useUserSearch();

    const { impersonateUser } = useImpersonateUser();

    const modalContentRef = useInfiniteScrollFetch<HTMLDivElement>({
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        scrollThresholdPx: SCROLL_THRESHOLD_PX,
    });

    useEffect(() => {
        setSearchInField(selectedColumn);
    }, [selectedColumn, setSearchInField]);

    const handleClose = useCallback(() => {
        fetchUsers("");
        onClose();
    }, [onClose, fetchUsers]);

    const handleSelectColumn = useCallback((column: string) => {
        setSelectedColumn(column);
    }, []);

    return (
        <Modal
            isOpen={isOpen}
            customStyles={ModalStyles}
        >
            <Modal.Header withBorder>
                <Modal.Title>{translate("Usermanagement.Modal.Impersonate.Title")}</Modal.Title>
                <StyledModalCloseButton onClick={handleClose} />
            </Modal.Header>
            <ModalContent>
                <FormWrapper>
                    <SearchColumnDropdown
                        columns={searchableColumns}
                        selectedColumn={selectedColumn}
                        onSelect={handleSelectColumn}
                    />
                    <SearchContainer
                        value={search}
                        placeholder={translate("Usermanagement.Modal.Impersonate.SearchText")}
                        onChange={fetchUsers}
                        loading={loading}
                    />
                </FormWrapper>
                <ScrollableContent ref={modalContentRef}>
                    {error ? <ErrorMessage message={error} /> : null}
                    <SearchResultsDisplay
                        search={search}
                        tableData={tableData}
                        loading={loading}
                        isFetchingNextPage={isFetchingNextPage}
                        onItemClick={impersonateUser}
                    />
                </ScrollableContent>
            </ModalContent>
        </Modal>
    );
};
